import { ref, onUnmounted } from 'vue'
import type { TimeRange, EditMode } from '@/types'

export function usePlayback(getAudioContext: () => AudioContext) {
  const isPlaying = ref(false)
  const currentTime = ref(0)
  const loop = ref(true)
  
  let audioSource: AudioBufferSourceNode | null = null
  let audioBuffer: AudioBuffer | null = null
  let startOffset = 0
  let playStartTime = 0
  let animationFrame: number | null = null
  let selection: TimeRange = { start: 0, end: 0 }
  let editMode: EditMode = 'cut'
  // For trim mode: track if we're in the first or second segment
  let trimSegment: 'first' | 'second' = 'first'
  
  // Set the audio buffer to play
  const setAudioBuffer = (buffer: AudioBuffer | null) => {
    stop()
    audioBuffer = buffer
    if (buffer) {
      selection = { start: 0, end: buffer.duration }
      currentTime.value = 0
    }
  }
  
  // Set the selection range
  const setSelection = (range: TimeRange) => {
    const wasPlaying = isPlaying.value
    const oldSelection = { ...selection }
    selection = { ...range }
    
    if (editMode === 'cut') {
      // Cut mode: playhead must be within selection
      if (currentTime.value < range.start) {
        // Playhead is before new start - move to start
        if (wasPlaying) {
          // Restart playback from new start
          pause()
          startOffset = range.start
          currentTime.value = range.start
          play()
        } else {
          startOffset = range.start
          currentTime.value = range.start
        }
      } else if (currentTime.value >= range.end) {
        // Playhead is at or past new end
        if (wasPlaying) {
          // When playing, loop back to start or clamp
          pause()
          if (loop.value) {
            startOffset = range.start
            currentTime.value = range.start
          } else {
            startOffset = range.end - 0.001
            if (startOffset < range.start) startOffset = range.start
            currentTime.value = startOffset
          }
          play()
        } else {
          // When paused, follow the end marker (stay at end position)
          startOffset = range.end
          currentTime.value = range.end
        }
      } else if (wasPlaying && range.end !== oldSelection.end) {
        // End marker moved while playhead is within bounds - restart with new duration
        const ctx = getAudioContext()
        const elapsed = ctx.currentTime - playStartTime
        const currentPos = startOffset + elapsed

        // Stop current source
        if (audioSource) {
          audioSource.onended = null
          try { audioSource.stop() } catch { /* ignore */ }
          audioSource.disconnect()
          audioSource = null
        }
        isPlaying.value = false
        if (animationFrame) {
          cancelAnimationFrame(animationFrame)
          animationFrame = null
        }

        startOffset = currentPos
        currentTime.value = currentPos
        play()
      }
      // If playhead is still within bounds and end didn't change, let playback continue smoothly
    } else {
      // Trim mode: playhead must be outside selection (in kept regions)
      if (currentTime.value >= range.start && currentTime.value <= range.end) {
        // Playhead is now in trimmed region - need to move it out
        // Determine which boundary to snap to based on which marker moved
        const startMarkerMoved = range.start !== oldSelection.start
        const endMarkerMoved = range.end !== oldSelection.end
        
        // Determine target position: stick to the marker that moved, or nearest boundary
        let targetPos: number
        let targetSegment: 'first' | 'second'
        
        if (endMarkerMoved && !startMarkerMoved) {
          // End marker moved past playhead - stick to end marker
          targetPos = range.end
          targetSegment = 'second'
        } else if (startMarkerMoved && !endMarkerMoved) {
          // Start marker moved past playhead - stick to start marker
          targetPos = range.start
          targetSegment = 'first'
        } else {
          // Both moved or neither moved - go to nearest boundary
          const distToStart = currentTime.value - range.start
          const distToEnd = range.end - currentTime.value
          if (distToEnd <= distToStart) {
            targetPos = range.end
            targetSegment = 'second'
          } else {
            targetPos = range.start
            targetSegment = 'first'
          }
        }
        
        if (wasPlaying) {
          // Stop current playback directly
          if (audioSource) {
            audioSource.onended = null
            try { audioSource.stop() } catch { /* ignore */ }
            audioSource.disconnect()
            audioSource = null
          }
          isPlaying.value = false
          if (animationFrame) {
            cancelAnimationFrame(animationFrame)
            animationFrame = null
          }
          
          trimSegment = targetSegment
          startOffset = targetPos
          currentTime.value = targetPos
          play()
        } else {
          // When paused, just update position
          trimSegment = targetSegment
          startOffset = targetPos
          currentTime.value = targetPos
        }
      }
      // If playhead is still in a valid kept region, let playback continue smoothly
      // The onended handler will naturally enforce boundaries when reached
    }
  }
  
  // Set the edit mode
  const setEditMode = (mode: EditMode) => {
    const wasPlaying = isPlaying.value
    if (wasPlaying) {
      pause()
    }
    
    editMode = mode
    
    if (mode === 'cut') {
      // Cut mode: ensure playhead is within selection
      if (currentTime.value < selection.start || currentTime.value > selection.end) {
        startOffset = selection.start
        currentTime.value = selection.start
      }
    } else {
      // Trim mode: ensure playhead is outside selection (in kept regions)
      if (currentTime.value >= selection.start && currentTime.value <= selection.end) {
        startOffset = 0
        currentTime.value = 0
        trimSegment = 'first'
      } else if (currentTime.value < selection.start) {
        trimSegment = 'first'
      } else {
        trimSegment = 'second'
      }
    }
    
    if (wasPlaying) {
      play()
    }
  }
  
  // Play audio
  const play = () => {
    if (!audioBuffer || isPlaying.value) return

    const ctx = getAudioContext()

    // Resume context if suspended
    if (ctx.state === 'suspended') {
      ctx.resume()
    }

    // Cancel any existing animation frame
    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
      animationFrame = null
    }

    // Create source node
    audioSource = ctx.createBufferSource()
    audioSource.buffer = audioBuffer
    audioSource.connect(ctx.destination)
    audioSource.loop = false

    let startPos: number
    let playDuration: number

    if (editMode === 'cut') {
      // Cut mode: play from current position to end of selection
      startPos = startOffset
      // Ensure startPos is within bounds
      if (startPos >= selection.end) {
        startPos = selection.start
        startOffset = selection.start
      }
      if (startPos < selection.start) {
        startPos = selection.start
        startOffset = selection.start
      }
      playDuration = selection.end - startPos
    } else {
      // Trim mode: play the kept segments
      if (trimSegment === 'first') {
        // First segment: play from current position to selection.start
        startPos = startOffset
        if (startPos >= selection.start) {
          startPos = 0
          startOffset = 0
        }
        playDuration = selection.start - startPos
      } else {
        // Second segment: play from current position to end of audio
        startPos = startOffset
        if (startPos < selection.end) {
          startPos = selection.end
          startOffset = selection.end
        }
        playDuration = audioBuffer.duration - startPos
      }
    }

    // Safety check: ensure playDuration is positive
    if (playDuration <= 0) {
      if (editMode === 'cut') {
        startPos = selection.start
        startOffset = selection.start
        playDuration = selection.end - selection.start
      } else {
        if (trimSegment === 'first') {
          startPos = 0
          startOffset = 0
          playDuration = selection.start
        } else {
          startPos = selection.end
          startOffset = selection.end
          playDuration = audioBuffer.duration - selection.end
        }
      }
    }

    currentTime.value = startPos

    // Start playback
    audioSource.start(0, startPos, playDuration)
    playStartTime = ctx.currentTime
    isPlaying.value = true

    // Handle playback end
    audioSource.onended = () => {
      if (isPlaying.value) {
        if (editMode === 'trim' && trimSegment === 'first') {
          // In trim mode, after first segment ends, jump to second segment
          trimSegment = 'second'
          startOffset = selection.end
          currentTime.value = selection.end
          isPlaying.value = false
          play()
        } else if (loop.value) {
          // Loop: restart from beginning
          if (editMode === 'cut') {
            startOffset = selection.start
            currentTime.value = selection.start
          } else {
            trimSegment = 'first'
            startOffset = 0
            currentTime.value = 0
          }
          isPlaying.value = false
          play()
        } else {
          // No loop: stop at appropriate position
          if (editMode === 'cut') {
            currentTime.value = selection.start
            startOffset = selection.start
          } else {
            trimSegment = 'first'
            currentTime.value = 0
            startOffset = 0
          }
          stop()
        }
      }
    }

    // Start time update loop
    updateTime()
  }
  
  // Pause audio
  const pause = () => {
    if (!isPlaying.value || !audioSource) return
    
    const ctx = getAudioContext()
    startOffset = startOffset + (ctx.currentTime - playStartTime)
    
    // Clamp to appropriate bounds based on mode
    if (editMode === 'cut') {
      if (startOffset >= selection.end) {
        startOffset = selection.end
      }
      if (startOffset < selection.start) {
        startOffset = selection.start
      }
    } else {
      // Trim mode
      if (trimSegment === 'first') {
        if (startOffset >= selection.start) {
          startOffset = selection.start - 0.001
          if (startOffset < 0) startOffset = 0
        }
      } else {
        if (startOffset < selection.end) {
          startOffset = selection.end
        }
      }
    }
    
    audioSource.onended = null
    audioSource.stop()
    audioSource.disconnect()
    audioSource = null
    isPlaying.value = false
    
    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
      animationFrame = null
    }
  }
  
  // Stop audio
  const stop = () => {
    if (audioSource) {
      audioSource.onended = null
      try {
        audioSource.stop()
      } catch {
        // Ignore errors if already stopped
      }
      audioSource.disconnect()
      audioSource = null
    }
    
    isPlaying.value = false
    
    if (editMode === 'cut') {
      startOffset = selection.start
      currentTime.value = selection.start
    } else {
      trimSegment = 'first'
      startOffset = 0
      currentTime.value = 0
    }
    
    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
      animationFrame = null
    }
  }
  
  // Toggle play/pause
  const togglePlay = () => {
    if (isPlaying.value) {
      pause()
    } else {
      play()
    }
  }
  
  // Seek to position
  const seek = (time: number) => {
    const wasPlaying = isPlaying.value
    
    if (wasPlaying) {
      pause()
    }
    
    if (editMode === 'cut') {
      // Cut mode: clamp to selection bounds
      startOffset = Math.max(selection.start, Math.min(selection.end, time))
    } else {
      // Trim mode: clamp to kept regions (0 to start, or end to duration)
      if (time < selection.start) {
        // First segment
        startOffset = Math.max(0, Math.min(selection.start, time))
        trimSegment = 'first'
      } else if (time >= selection.end) {
        // Second segment
        startOffset = Math.max(selection.end, Math.min(audioBuffer?.duration || selection.end, time))
        trimSegment = 'second'
      } else {
        // Time is in trimmed region - snap to nearest kept region
        const distToStart = time - selection.start
        const distToEnd = selection.end - time
        if (distToStart <= distToEnd) {
          startOffset = selection.start - 0.001 // Just before selection start
          if (startOffset < 0) startOffset = 0
          trimSegment = 'first'
        } else {
          startOffset = selection.end
          trimSegment = 'second'
        }
      }
    }
    currentTime.value = startOffset
    
    if (wasPlaying) {
      play()
    }
  }
  
  // Update current time during playback
  const updateTime = () => {
    if (!isPlaying.value) return
    
    const ctx = getAudioContext()
    const elapsed = ctx.currentTime - playStartTime
    currentTime.value = startOffset + elapsed
    
    if (editMode === 'cut') {
      // Check if we've reached the end of selection
      if (currentTime.value >= selection.end) {
        // Actively stop the audio source (it may still be playing if end marker was moved)
        if (audioSource) {
          audioSource.onended = null
          try { audioSource.stop() } catch { /* ignore */ }
          audioSource.disconnect()
          audioSource = null
        }
        if (loop.value) {
          startOffset = selection.start
          currentTime.value = selection.start
          isPlaying.value = false
          play()
        } else {
          currentTime.value = selection.start
          startOffset = selection.start
          isPlaying.value = false
        }
        return // Don't continue the animation frame
      }
    } else {
      // Trim mode: check bounds for current segment
      if (trimSegment === 'first') {
        if (currentTime.value >= selection.start) {
          // Reached the start marker - transition to second segment
          // This handles the case where selection.start changed during drag
          if (audioSource) {
            audioSource.onended = null
            try { audioSource.stop() } catch { /* ignore */ }
            audioSource.disconnect()
            audioSource = null
          }
          trimSegment = 'second'
          startOffset = selection.end
          currentTime.value = selection.end
          isPlaying.value = false
          play()
          return // Don't continue the animation frame - play() starts a new one
        }
      } else {
        if (currentTime.value >= (audioBuffer?.duration || selection.end)) {
          // Reached end of audio in second segment
          if (audioSource) {
            audioSource.onended = null
            try { audioSource.stop() } catch { /* ignore */ }
            audioSource.disconnect()
            audioSource = null
          }
          if (loop.value) {
            // Loop back to beginning (first segment)
            trimSegment = 'first'
            startOffset = 0
            currentTime.value = 0
            isPlaying.value = false
            play()
          } else {
            // Stop playback
            trimSegment = 'first'
            currentTime.value = 0
            startOffset = 0
            isPlaying.value = false
          }
          return // Don't continue the animation frame
        }
      }
    }
    
    animationFrame = requestAnimationFrame(updateTime)
  }
  
  // Toggle loop
  const toggleLoop = () => {
    loop.value = !loop.value
  }
  
  // Note: Selection change handling is done in setSelection function
  // which is called from App.vue whenever the selection changes
  
  // Cleanup on unmount
  onUnmounted(() => {
    stop()
  })
  
  return {
    isPlaying,
    currentTime,
    loop,
    setAudioBuffer,
    setSelection,
    setEditMode,
    play,
    pause,
    stop,
    togglePlay,
    seek,
    toggleLoop
  }
}
